import React, { useMemo, useState } from 'react';
import { Button, Card, Form, Input, Space, Steps, Typography } from 'antd';
import { ArrowLeftOutlined, KeyOutlined, LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { ApiConfig } from '../../services/ApiConfigs';
import { ApiServices } from '../../services/apiServices';
import { errorMsg, successMsg } from '../../utils/ShowMessage';
import { routes } from '../../utils/routes.config';

const { Title, Text } = Typography;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const steps = useMemo(() => ([
    { title: 'Email' },
    { title: 'OTP' },
    { title: 'New password' },
  ]), []);

  const requestOtp = async (values) => {
    setLoading(true);
    try {
      const e = String(values.email || '').trim().toLowerCase();
      setEmail(e);
      const res = await ApiServices('post', ApiConfig.FORGOT_PASSWORD_REQUEST, { email: e });
      if (res?.success) {
        successMsg(res?.message || 'OTP sent');
        setStep(1);
      } else {
        errorMsg(res?.message || 'Failed to send OTP');
      }
    } catch (err) {
      errorMsg(err?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (values) => {
    setLoading(true);
    try {
      const res = await ApiServices('post', ApiConfig.FORGOT_PASSWORD_RESET, {
        email,
        otp: values.otp,
        password: values.password,
      });
      if (res?.success) {
        successMsg(res?.message || 'Password reset');
        navigate(routes.LOGIN);
      } else {
        errorMsg(res?.message || 'Failed to reset password');
      }
    } catch (err) {
      errorMsg(err?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-grid-pattern" />
      </div>

      <div className="login-top-bar">
        <Link to={routes.LOGIN} className="back-to-home">
          <ArrowLeftOutlined /> Back to Sign In
        </Link>
        <Link to={routes.HOME} className="login-docs-link">
          Home
        </Link>
      </div>

      <div className="login-container">
        <Card className="login-card" bordered={false}>
          <div className="login-header">
            <div className="login-logo-wrapper">
              <SafetyOutlined className="login-logo-icon" />
            </div>
            <div className="login-brand-tag">
              <span className="brand-dot" />
              <span>Password Recovery</span>
            </div>
            <Title level={2} className="login-title">Reset Your Password</Title>
            <Text className="login-subtitle">Follow the steps below using the OTP verification code sent to your email</Text>
          </div>

          <div style={{ marginBottom: 20 }}>
            <Steps current={step} items={steps} size="small" />
          </div>

          {step === 0 && (
            <Form layout="vertical" onFinish={requestOtp} size="large" initialValues={{ email }}>
              <Form.Item
                name="email"
                label={<span className="login-field-label">Email Address</span>}
                rules={[{ required: true, message: 'Please enter your email' }, { type: 'email', message: 'Please enter a valid email' }]}
              >
                <Input prefix={<MailOutlined className="login-input-icon" />} placeholder="Enter your registered email" className="login-input" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large" icon={<KeyOutlined />} className="login-submit-button">
                Send Verification OTP
              </Button>
            </Form>
          )}

          {step === 1 && (
            <Form
              layout="vertical"
              onFinish={() => { setStep(2); }}
              size="large"
              initialValues={{ otp: '' }}
            >
              <Form.Item label={<span className="login-field-label">Email Address</span>}>
                <Input value={email} disabled className="login-input" />
              </Form.Item>
              <Form.Item 
                name="otp" 
                label={<span className="login-field-label">6-Digit OTP</span>} 
                rules={[{ required: true, message: 'Please input the 6-digit code' }, { len: 6, message: 'OTP must be exactly 6 digits' }]}
              >
                <Input prefix={<KeyOutlined className="login-input-icon" />} placeholder="Enter 6-digit code" maxLength={6} className="login-input" />
              </Form.Item>
              <Space style={{ width: '100%', marginTop: 6 }} direction="vertical" size={10}>
                <Button type="primary" htmlType="submit" block size="large" icon={<ArrowLeftOutlined rotate={180} />} className="login-submit-button">
                  Verify & Proceed
                </Button>
                <Button onClick={() => setStep(0)} block size="large" style={{ borderRadius: 10, height: 44 }}>
                  Change Email
                </Button>
              </Space>
            </Form>
          )}

          {step === 2 && (
            <Form layout="vertical" onFinish={resetPassword} size="large" initialValues={{ otp: '', password: '' }}>
              <Form.Item label={<span className="login-field-label">Email Address</span>}>
                <Input value={email} disabled className="login-input" />
              </Form.Item>
              <Form.Item 
                name="otp" 
                label={<span className="login-field-label">Verification OTP</span>} 
                rules={[{ required: true, message: 'Please enter the OTP' }, { len: 6, message: 'OTP must be 6 digits' }]}
              >
                <Input prefix={<KeyOutlined className="login-input-icon" />} placeholder="6-digit OTP" maxLength={6} className="login-input" />
              </Form.Item>
              <Form.Item 
                name="password" 
                label={<span className="login-field-label">New Password</span>} 
                rules={[{ required: true, message: 'Please enter new password' }, { min: 6, message: 'Must be at least 6 characters' }]}
              >
                <Input.Password prefix={<LockOutlined className="login-input-icon" />} placeholder="Enter new secure password" className="login-input" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large" icon={<LockOutlined />} className="login-submit-button">
                Set New Password & Log In
              </Button>
            </Form>
          )}
        </Card>
      </div>
    </div>
  );
}

